import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, CheckCircle2, XCircle, HeartHandshake, AlertTriangle, MessageCircle } from "lucide-react";
import { api } from "../api";
import MobileShell from "../components/MobileShell";
import AaryaMark from "../components/AaryaMark";
import { buildAaryaOptInLink } from "../constants/whatsapp";

// Public landing page shown when a Trust-Circle invitee taps the link in their
// SMS. NO login required. Route is /i/{token}.
export default function InviteAccept({ token }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get(`/circle-invite/${token}`)
      .then((r) => { if (mounted) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => {
        if (!mounted) return;
        const msg = e?.response?.data?.detail || "This invite link is invalid or expired.";
        setState({ loading: false, data: null, error: msg });
      });
    return () => { mounted = false; };
  }, [token]);

  const act = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/circle-invite/${token}/${action}`);
      setState((s) => ({
        ...s,
        data: {
          ...s.data,
          contact: {
            ...s.data.contact,
            invite_status: r.data.status,
          },
        },
      }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobileShell>
      <div className="px-6 py-8" data-testid="invite-accept-screen">
        <div className="flex items-center gap-2">
          <AaryaMark size={40}/>
          <div>
            <p className="font-display text-[22px] text-ink-900 leading-none">Aarya</p>
            <p className="text-[10px] tracking-[0.16em] uppercase text-ink-500">A gift from IndoBlitz</p>
          </div>
        </div>

        {state.loading && (
          <div className="mt-10 text-center text-ink-500" data-testid="invite-loading">
            Loading your invite…
          </div>
        )}

        {state.error && (
          <div className="mt-10 rounded-3xl bg-brick-50 border border-brick-500/30 p-5" data-testid="invite-error">
            <div className="flex items-center gap-2 text-brick-700">
              <AlertTriangle size={20}/>
              <p className="font-semibold">Invite unavailable</p>
            </div>
            <p className="text-sm text-ink-700 mt-2">{state.error}</p>
            <p className="text-[12px] text-ink-500 mt-4">
              Please ask the person who invited you to send a fresh link.
            </p>
          </div>
        )}

        {state.data && (
          <InviteBody data={state.data} onAct={act} busy={busy} token={token}/>
        )}
      </div>
    </MobileShell>
  );
}

function InviteBody({ data, onAct, busy, token }) {
  const { contact, inviter } = data;
  const status = contact.invite_status;

  if (status === "active") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="mt-8 rounded-3xl bg-sage-50 border border-sage-400/30 p-6"
        data-testid="invite-accepted-card"
      >
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-sage-500 text-white flex items-center justify-center">
            <CheckCircle2 size={22}/>
          </div>
          <p className="font-display text-[22px] text-ink-900">You&apos;re in {inviter.name}&apos;s circle.</p>
        </div>
        <p className="text-sm text-ink-700 mt-3">
          You&apos;ll now receive Aarya safety pings and any emergency alerts from {inviter.name}.
          If they trigger the silent SOS, you&apos;ll be one of the first to know - so please keep this phone handy.
        </p>
        <p className="text-[13px] text-ink-500 mt-4">
          Want to receive updates on WhatsApp instead of SMS? Tap the button below - it opens WhatsApp
          with a pre-filled message. Just hit Send. You&apos;re done.
        </p>
        <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 leading-snug" data-testid="invite-dual-sim-warning">
          <strong>Dual-SIM phone?</strong> WhatsApp opens whichever account is currently active. If you use
          multiple WhatsApp accounts, switch to the one that received this invite <em>before</em> tapping.
          (Don&apos;t worry - even if you send from another number, we can still match your invite from the message.)
        </div>
        <a
          href={buildAaryaOptInLink(inviter.name, token)}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="invite-wa-optin"
          className="mt-4 w-full h-[56px] rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[15px] font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition shadow-lifted"
        >
          <MessageCircle size={20}/>
          Enable WhatsApp alerts (one tap)
        </a>
        <a
          href="https://aarya.indoblitz.com"
          className="mt-3 inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white border border-sand-200 text-ink-900 text-sm font-semibold"
          data-testid="invite-installed-cta"
        >
          <ShieldCheck size={18}/>
          Install Aarya for yourself
        </a>
      </motion.div>
    );
  }

  if (status === "declined") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="mt-8 rounded-3xl bg-sand-100 border border-sand-200 p-6"
        data-testid="invite-declined-card"
      >
        <div className="flex items-center gap-2">
          <XCircle size={22} className="text-ink-500"/>
          <p className="font-display text-[22px] text-ink-900">You&apos;ve declined this invite.</p>
        </div>
        <p className="text-sm text-ink-700 mt-3">
          {inviter.name} has been informed. You will NOT receive any Aarya messages on their behalf.
        </p>
        <p className="text-[13px] text-ink-500 mt-4">
          Changed your mind? Ask {inviter.name} to send you a fresh invite.
        </p>
      </motion.div>
    );
  }

  // status === "pending" - the main choice screen
  // WhatsApp is the PRIMARY path. Tapping it accepts the invite server-side
  // AND opens WhatsApp - so if the invitee successfully sends the message,
  // the webhook additionally sets wa_opted_in_at (WA delivery from then on).
  // If they don't send it, they're still accepted, just staying on SMS.
  const waHref = buildAaryaOptInLink(inviter.name, token);
  const acceptAndOpenWa = async () => {
    // Fire-and-open. We deliberately open the WA link BEFORE awaiting the
    // accept POST because iOS Safari blocks window.open outside a user-gesture
    // tick. The accept API runs in parallel; if it fails the user simply sees
    // the "pending" page on refresh.
    window.open(waHref, "_blank", "noopener,noreferrer");
    onAct("accept");
  };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
      <div className="rounded-3xl bg-terracotta-50 border border-terracotta-400/30 p-6" data-testid="invite-pending-card">
        <div className="flex items-center gap-2">
          <HeartHandshake size={22} className="text-terracotta-600"/>
          <p className="text-[11px] tracking-[0.18em] uppercase text-terracotta-600 font-semibold">
            You&apos;ve been invited
          </p>
        </div>
        <p className="font-display text-[26px] text-ink-900 leading-tight mt-3">
          <span className="text-sage-700">{inviter.name}</span>
          <br/>added you to their Trust Circle.
        </p>
        <p className="text-sm text-ink-700 mt-4 leading-relaxed">
          Aarya is a safety app by IndoBlitz. If {inviter.name} taps &quot;I am Safe&quot;, asks for a call,
          or triggers a silent SOS, you&apos;ll get an alert from us on their behalf - with their
          location if it&apos;s an emergency.
        </p>
        <ul className="text-[13px] text-ink-700 mt-4 space-y-1.5">
          <li>• You never need to install anything.</li>
          <li>• At most 1-2 messages a day (a gentle &quot;I&apos;m okay&quot; check-in).</li>
          <li>• You can decline now, or leave anytime by replying STOP.</li>
        </ul>
      </div>

      {/* Primary CTA - WhatsApp path. Emerald button, prominent. */}
      <div className="mt-6 rounded-3xl bg-emerald-50 border-2 border-emerald-500/50 p-5" data-testid="invite-wa-primary-card">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle size={18} className="text-emerald-700"/>
          <p className="text-[11px] tracking-[0.18em] uppercase text-emerald-700 font-bold">
            Recommended · Free forever
          </p>
        </div>
        <p className="text-[15px] text-ink-900 font-semibold leading-snug">
          Accept + get alerts on WhatsApp
        </p>
        <p className="text-[12px] text-ink-700 mt-1 leading-snug">
          Free WhatsApp delivery worldwide. Rich formatting, map links, delivery receipts.
          One tap opens WhatsApp with a ready message - just hit <strong>Send</strong>.
        </p>
        <button
          onClick={acceptAndOpenWa}
          disabled={busy}
          data-testid="invite-accept-wa-btn"
          className="mt-4 w-full h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-60 shadow-lifted"
        >
          <MessageCircle size={20}/>
          Accept &amp; enable WhatsApp
        </button>
        <p className="text-[10px] text-emerald-800 mt-2 text-center">
          Dual-SIM phone? Switch to the right WhatsApp account before tapping.
        </p>
      </div>

      {/* Secondary choices - accept-only or decline */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          onClick={() => onAct("accept")}
          disabled={busy}
          data-testid="invite-accept-btn"
          className="h-12 rounded-full bg-white border border-sand-200 text-ink-900 text-[13px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition disabled:opacity-60"
        >
          <CheckCircle2 size={16}/>
          Accept, SMS only
        </button>
        <button
          onClick={() => onAct("decline")}
          disabled={busy}
          data-testid="invite-decline-btn"
          className="h-12 rounded-full bg-white border border-brick-500/30 text-brick-700 text-[13px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition disabled:opacity-60"
        >
          <XCircle size={16}/>
          Decline
        </button>
      </div>

      <p className="text-center text-[11px] text-ink-500 mt-6">
        We take your privacy seriously. Aarya only stores your name, phone number, and relation as
        entered by {inviter.name}. Learn more at aarya.indoblitz.com.
      </p>
    </motion.div>
  );
}
